import React, { useMemo } from "react";
import DisplayText from "../../components/DisplayText/DisplayText";
import { Button, Dialog, DialogActions, DialogContent, CircularProgress, IconButton, Divider } from "@mui/material";
import { IMediaOctFile } from "../studies/studiesSlice";
import { useGetSiteUsersQuery } from "./dataAccess";
import { Box } from "@mui/system";
import { IUser } from "../auth/login/loginSlice";
import { ContentCopy as ContentCopyIcon } from "@mui/icons-material";
import ToolTip from "../../components/Tooltip/Tooltip";
import { figureFlavor } from "../../utils/MediaUtils";

interface ICopyCaseInfoProps {
    isOpen: boolean;
    study: IMediaOctFile;
    onClose: () => void;
    visitName: string;
    visitUuid: string;
    siteName: string;
    patientId: string;
}

const CopyCaseInfo: React.FC<ICopyCaseInfoProps> = ({
    isOpen,
    study,
    visitName,
    visitUuid,
    siteName,
    onClose,
    patientId,
}) => {
    const { data: siteUsers, isLoading: siteUsersIsLoading } = useGetSiteUsersQuery(study.uuid, {
        refetchOnMountOrArgChange: true,
        skip: !isOpen,
    });

    const queryType = useMemo(() => {
        const flavor = figureFlavor(study);

        const isOct = flavor === "oct-mp4" || flavor === "oct-photo";

        if (isOct) {
            return "oct";
        }

        return "other";
    }, [study]);

    const mailList = useMemo(() => {
        if (!siteUsers) return "";

        const usersMails = siteUsers.map((user: IUser) => user.email);

        return usersMails.join(", ");
    }, [siteUsers]);

    const dialogContent = useMemo(() => {
        if (!study || !mailList) {
            return {
                to: "",
                subject: "",
                body: "",
            };
        }

        return {
            to: mailList,
            subject: `iNebula Query Notification - ${study.trialName} - ${siteName} - ${patientId} - ${
                new Date(study!.ScanDateAndTime).toISOString().split("T")[0]
            } - ${study.FileName} - ${study.Laterality}`,
            body: `Dear team member,

This email is to notify you that the following case was queried and returned to your work state for correction:

Protocol: ${study.trialName || "-"}
Site #: ${siteName}
Visit: ${visitName}
Subject ID: ${patientId}
Visit Date: ${study.ScanDateAndTime ? new Date(study!.ScanDateAndTime).toISOString().split("T")[0] : "-"}

Reason:

Kindly respond to this email and return the corrected case in Nebula within 3 business days. Upon re-uploading the new case to Nebula, append “New” to the naming convention:

E.g. if responding to ${queryType === "oct" ? "an OCT image query" : "this query"}, the new folder will reflect:

${study!.fullUploadPath.split(".")?.[0] || ""}_New.${study!.fullUploadPath.split(".")?.[1] || ""}

After re-uploading has been completed, “reply all” to this email to notify all site personnel and the PM.

Sincerely,`,
        };
    }, [study, siteUsers]);

    const handleCopyEmailSubject = () => {
        navigator.clipboard.writeText(dialogContent.subject);
    };

    const handleCopyEmailTo = () => {
        navigator.clipboard.writeText(dialogContent.to);
    };

    const handleCopyEmailBody = () => {
        navigator.clipboard.writeText(dialogContent.body);
    };

    if (!isOpen) return null;

    return (
        <Dialog maxWidth="lg" fullWidth open={true} onClose={onClose}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", p: 24, pb: 0 }}>
                <DisplayText text="Email template" type="h5" />
            </Box>
            <DialogContent>
                {siteUsersIsLoading ? (
                    <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <>
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 10,
                                justifyContent: "space-between",
                                my: 6,
                            }}
                        >
                            <p>
                                <span style={{ fontWeight: "bold" }}>To: </span>
                                <span>{dialogContent.to}</span>
                            </p>
                            <ToolTip content={`Copy email list`}>
                                <IconButton onClick={handleCopyEmailTo} size="small">
                                    <ContentCopyIcon />
                                </IconButton>
                            </ToolTip>
                        </Box>
                        <Divider />
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 10,
                                justifyContent: "space-between",
                                my: 6,
                            }}
                        >
                            <p>
                                <span style={{ fontWeight: "bold" }}>Subject: </span>
                                <span>{dialogContent.subject}</span>
                            </p>
                            <ToolTip content={`Copy email subject`}>
                                <IconButton onClick={handleCopyEmailSubject} size="small">
                                    <ContentCopyIcon />
                                </IconButton>
                            </ToolTip>
                        </Box>
                        <Divider />
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "start",
                                gap: 10,
                                justifyContent: "space-between",
                                mt: 16,
                            }}
                        >
                            <p style={{ whiteSpace: "pre-line" }}>{dialogContent.body}</p>
                            <ToolTip content={`Copy email body`}>
                                <IconButton onClick={handleCopyEmailBody} size="small">
                                    <ContentCopyIcon />
                                </IconButton>
                            </ToolTip>
                        </Box>
                    </>
                )}
            </DialogContent>
            <DialogActions>
                <Button variant="contained" onClick={onClose}>
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default CopyCaseInfo;
